import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArticleCard } from '../components/ArticleCard';
import { 
  ArrowRight, BookOpen, Users, FileText, Brain, 
  Sparkles, Send, Eye, TrendingUp, Calendar, Award
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const [stats, setStats] = useState({ published_articles: 0, authors: 0, resources: 0, subscribers: 0, total_citations: 0 });
  const [recentArticles, setRecentArticles] = useState([]);
  const [impactData, setImpactData] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRecentArticles();
    fetchImpactFactor();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/stats`);
      if (res.ok) setStats(await res.json());
    } catch (e) {
      console.error('Failed to fetch stats:', e);
    }
  };

  const fetchRecentArticles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/articles?status=published&limit=6`);
      if (res.ok) {
        const data = await res.json();
        setRecentArticles(data.articles || []);
      }
    } catch (e) {
      console.error('Failed to fetch articles:', e);
    }
  };

  const fetchImpactFactor = async () => {
    try {
      const res = await fetch(`${API_URL}/api/citations/impact-factor`);
      if (res.ok) setImpactData(await res.json());
    } catch (e) {
      console.error('Failed to fetch impact factor:', e);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        toast.success('Inscription réussie ! Bienvenue dans notre newsletter.');
        setEmail('');
      } else {
        throw new Error('Failed');
      }
    } catch (e) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-background">
      {/* Hero Banner - MDPI Style */}
      <section className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <Badge className="bg-white/20 text-white border-0 mb-4">
                Revue scientifique en libre accès
              </Badge>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Journal de pratiques et recherches en psychothérapie
              </h1>
              <p className="text-lg text-primary-foreground/90 mb-6 max-w-2xl">
                La recherche au rythme de la rencontre thérapeutique et des avancées technologiques.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/articles">
                  <Button size="lg" variant="secondary" className="gap-2">
                    <BookOpen className="w-4 h-4" />
                    Parcourir les articles
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 gap-2">
                    <FileText className="w-4 h-4" />
                    Soumettre un manuscrit
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats.published_articles}</p>
                  <p className="text-sm text-white/80">Articles publiés</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats.authors}</p>
                  <p className="text-sm text-white/80">Auteurs</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{stats.total_citations}</p>
                  <p className="text-sm text-white/80">Citations</p>
                </CardContent>
              </Card>
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{impactData ? impactData.citescore : '—'}</p>
                  <p className="text-sm text-white/80">CiteScore</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Journal Info Bar */}
      <section className="border-b bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-primary" />
              <span>En attente d'ISSN</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span>Impact Factor : {impactData ? impactData.impact_factor : '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary" />
              <span>Accès libre (Open Access)</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary" />
              <span>Publication continue</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <span>Évaluation par les pairs</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area - Two Column Layout */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Main Column - Articles */}
          <div className="lg:col-span-3">
            {/* Latest Articles */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-serif text-2xl font-semibold">Dernières publications</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Articles récemment publiés dans le journal
                  </p>
                </div>
                <Link to="/articles">
                  <Button variant="outline" size="sm" className="gap-1">
                    Voir tout <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {recentArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {recentArticles.map((article) => (
                    <ArticleCard key={article.article_id} article={article} />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      Les premiers articles seront bientôt disponibles.
                    </p>
                    <Link to="/register" className="inline-block mt-4">
                      <Button variant="outline">Devenir le premier auteur</Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Types of Contributions */}
            <div>
              <h2 className="font-serif text-2xl font-semibold mb-6">Types de contributions</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { type: 'research', title: 'Recherche empirique', desc: 'Études quantitatives et qualitatives' },
                  { type: 'case_study', title: 'Études de cas', desc: 'Analyses cliniques détaillées' },
                  { type: 'review', title: 'Revues de littérature', desc: 'Synthèses et méta-analyses' },
                  { type: 'practice_note', title: 'Notes de pratique', desc: 'Retours d\'expérience' },
                  { type: 'protocol', title: 'Protocoles', desc: 'Méthodologies de recherche' },
                  { type: 'innovation', title: 'Innovations', desc: 'Technologies thérapeutiques' }
                ].map((item, idx) => (
                  <Card key={idx} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <h3 className="font-medium text-sm mb-1">{item.title}</h3>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* About Journal Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg">À propos du journal</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-3">
                <p>
                  Le JPRP est un espace dédié aux praticiens, chercheurs et étudiants 
                  en psychothérapie.
                </p>
                <p>
                  Nous privilégions des contributions ancrées dans l'expérience clinique 
                  ou la recherche empirique.
                </p>
                <Link to="/about">
                  <Button variant="link" className="px-0 h-auto text-primary">
                    En savoir plus →
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Focus Areas */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg">Domaines d'intérêt</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {['TCC', 'Psychologie clinique', 'Santé mentale', 'Innovation', 'IA en thérapie', 
                    'Réalité virtuelle', 'Transdiagnostique', 'Bien-être'].map((topic, idx) => (
                    <Badge key={idx} variant="secondary" className="text-xs">
                      {topic}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Newsletter */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Send className="w-4 h-4 text-primary" />
                  Newsletter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Recevez les dernières publications directement dans votre boîte mail.
                </p>
                <form onSubmit={handleSubscribe} className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Votre email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white"
                    data-testid="newsletter-email-input"
                  />
                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={subscribing}
                    data-testid="newsletter-submit-btn"
                  >
                    {subscribing ? 'Inscription...' : 'S\'inscrire'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Submit Article CTA */}
            <Card className="bg-secondary/10 border-secondary/30">
              <CardContent className="p-6 text-center">
                <FileText className="w-10 h-10 mx-auto text-secondary mb-3" />
                <h3 className="font-serif font-semibold mb-2">Soumettre un article</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Partagez vos recherches avec notre communauté.
                </p>
                <Link to="/register">
                  <Button variant="outline" className="w-full">
                    Commencer
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-muted/30 border-y">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-serif font-semibold text-lg mb-2">Rigueur scientifique</h3>
              <p className="text-sm text-muted-foreground">
                Des contributions ancrées dans l'expérience clinique ou la recherche empirique.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                <Brain className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="font-serif font-semibold text-lg mb-2">Approche TCC</h3>
              <p className="text-sm text-muted-foreground">
                Dans la tradition des thérapies comportementales et cognitives, ouverte aux nouvelles approches.
              </p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-accent/30 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-7 h-7 text-accent-foreground" />
              </div>
              <h3 className="font-serif font-semibold text-lg mb-2">Innovation</h3>
              <p className="text-sm text-muted-foreground">
                Réalité virtuelle, intelligence artificielle, dispositifs numériques en psychothérapie.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 text-center">
        <h2 className="font-serif text-2xl font-semibold mb-3">
          Notre ambition est simple
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
          Encourager le partage d'expériences et la réflexion critique, au service d'une 
          psychothérapie toujours plus vivante, éclairée et humaine.
        </p>
        <Link to="/about">
          <Button variant="outline" size="lg">
            Découvrir notre ligne éditoriale
          </Button>
        </Link>
      </section>
    </div>
  );
}
